/**
 * @author Bismark A. FRIMPONG
 * @email info@bafrimpong.com
 * @create date 2024-05-17 
 * @modify date 2024-05-27
 * @desc Academy-v2 implementation of Mixpanel to track user clicks on some specific buttons and links for analytics
*/
import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  MIXPANEL_BTN_CLICKED, 
  MIXPANEL_PAGE_VERSION_DESKTOP, 
  UserObject, 
  identifyUser, 
  trackEvent 
} from './MixpanelUtils';
import {EventProps} from "./MixpanelUtils";

const MixpanelTrackingContext = createContext({});

/* eslint-disable react-refresh/only-export-components */
export const useMixpanelTracking = () => {
  return useContext(MixpanelTrackingContext);
};

type Props = {
  children: React.ReactNode
}

interface AllProps {
  eventProperties: string,
  userProfile: string
}

/**
 * Provides a context for mixpanel to be used as provider in handling mixpanel events
 * @returns {objec} returns a provider element
 */
export const MixpanelTrackingProvider: React.FC<Props> = ({ children }) => {
    
  const [trackProps, setTrackProps] = useState<string | AllProps>("");
  const [trackEventType, setTrackEventType] = useState<string>("");
  const [trackPageVersion, setTrackPageVersion] = useState<string>("");

  function setEventPropsData(target: HTMLElement) {

    //   const parentWithTrackProps = target.closest('[data-track-props]');
    const dataTrackProps = target.getAttribute('data-track-props');
    if (dataTrackProps) {
        try {
            const parsedProps = JSON.parse(dataTrackProps) as AllProps;
            setTrackProps(parsedProps);
        } catch (error) {
            console.error('Failed to parse data-track-props:', error);
        }
    }
  }

  const handleParsedProps = (properties: AllProps, target: HTMLElement) => {

    let parsedEventProperties: EventProps;
    let parsedUserProfile: UserObject;

    try {
      // Check if properties are already parsed
      if (typeof properties.eventProperties === 'string') {
          parsedEventProperties = JSON.parse(properties.eventProperties) as EventProps;
      } else {
          parsedEventProperties = properties.eventProperties;
      }

      if (typeof properties.userProfile === 'string') {
          parsedUserProfile = JSON.parse(properties.userProfile) as UserObject;
      } else {
          parsedUserProfile = properties.userProfile;
      }

      const allObjects = {
          eventProperties: parsedEventProperties,
          userProfile: parsedUserProfile
      };

      const additionalEventProps = {
        elementId: target.id,
        elementText: target.innerText,
        pageVersion: trackPageVersion || MIXPANEL_PAGE_VERSION_DESKTOP
      }
      
      const eventProps: EventProps = {...allObjects.eventProperties, ...additionalEventProps};
    
      if(target.hasAttribute("data-track-props")){
          // identify user and set its profile
          identifyUser(allObjects.userProfile);
    
          // track the event
          trackEvent(trackEventType || MIXPANEL_BTN_CLICKED, eventProps);
      }
    } catch (error) {
        console.error('Failed to parse properties:', error);
    }
  };

  useEffect(() => {

    const handleClick = (event: Event) => {

      const target = event.target as HTMLElement;
      
      try {
        
        // this section SHOULD NOT be modified else it will throw undefined errors
        // the reason for this check is that I realised some paragraph <p></p> element are placed inside a div <div></div>
        // and when you click on the div with the data-track-props, it triggers mixpanel but when you
        // click on the paragraph, it gives you undedefined error. To avoid this, I had to check if its parent has the
        // data-track-props attribute and use it.
        if(!target.hasAttribute("data-track-props")){
          
          if (target.parentElement?.hasAttribute("data-track-props")) {
           setEventPropsData(target);
          }
  
          if (target.parentElement?.hasAttribute("data-track-eventtype")) {
            setTrackEventType(target.parentElement.getAttribute("data-track-eventtype") as string);
          }
  
          if (target.parentElement?.hasAttribute("data-track-pageversion")) {
            setTrackPageVersion(target.parentElement.getAttribute("data-track-pageversion") as string);
          }
  
        } else {

          setEventPropsData(target);

          setTrackEventType(target.getAttribute("data-track-eventtype") as string)
          setTrackPageVersion(target.getAttribute("data-track-pageversion") as string);
        }

        if (target) {
            if (typeof trackProps === 'object') {
                handleParsedProps(trackProps, target);
            }
        }
      } catch (e) {
        // throw new Error("Error in Mixpanel Tracking Provider. " + e as string);
        console.error(e);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackProps]);

  
  return (
    <MixpanelTrackingContext.Provider value={{}}>
      {children}
    </MixpanelTrackingContext.Provider>
  );
};
